import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import washService from "../../../services/wash.service";
import { IWashRequest } from "../../../types/wash.types";
import { logger } from "../../../utils/logger";
import {
  hasCustomerReview,
  hasPartnerReview,
  isRatingSkipped,
} from "../../../components/WashHistoryTable/reviewUtils";

const WashDetail = () => {
  const { washId } = useParams<{ washId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [wash, setWash] = useState<IWashRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (washId) {
      fetchWashDetail();
    }
    // Re-runs only when washId changes; fetchWashDetail is a plain
    // (non-memoized) function recreated every render, so adding it here
    // would refetch on every render instead of only when the route param
    // changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washId]);

  const fetchWashDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await washService.getWashById(washId!);
      setWash(data);
      logger.info("Fetched wash detail", washId);
    } catch (err: any) {
      setError(err.message || "Failed to fetch wash details");
      logger.error("Failed to fetch wash detail", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !washId) return;

    try {
      setUpdating(true);
      await washService.updateWashStatus(washId, newStatus);
      await fetchWashDetail();
      setUpdateDialogOpen(false);
      setNewStatus("");
    } catch (err: any) {
      setError(err.message || "Failed to update status");
      logger.error("Failed to update wash status", err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (
    status: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "warning"
    | "info"
    | "success" => {
    switch (status) {
      case "pending": return "warning";
      case "accepted": return "info";
      case "in_progress": return "primary";
      case "completed": return "success";
      case "cancelled":
      case "not_arrive": return "error";
      default: return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending": return t("washDetail.statusPending");
      case "accepted": return t("washDetail.statusAccepted");
      case "in_progress": return t("washDetail.statusInProgress");
      case "completed": return t("washDetail.statusCompleted");
      case "cancelled": return t("washDetail.statusCancelled");
      case "not_arrive": return t("washDetail.statusNotArrived");
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!wash) {
    return (
      <Box className="p-4">
        <Alert severity="error">{t("washDetail.washNotFound")}</Alert>
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Paper className="p-6">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                const from = (location.state as { from?: string } | null)?.from;
                navigate(from ?? "/dashboard/washes");
              }}
            >
              {t("washDetail.back")}
            </Button>
            <Typography variant="h5">{t("washDetail.title")}</Typography>
          </Box>
          {wash.status !== "completed" && wash.status !== "cancelled" && (
            <Button
              variant="contained"
              onClick={() => setUpdateDialogOpen(true)}
            >
              {t("washDetail.updateStatus")}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={12}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">{t("washDetail.status")}</Typography>
                  <Chip
                    label={getStatusLabel(wash.status)}
                    color={getStatusColor(wash.status)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.customerInfo")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography>
                  <strong>{t("washDetail.name")}</strong> {wash.customer.firstName}{" "}
                  {wash.customer.lastName}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.phone")}</strong> {wash.customer.country_code}{" "}
                  {wash.customer.phone}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.customerId")}</strong> {wash.customer._id}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.carWashInfo")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {wash.carWash ? (
                  <>
                    <Typography>
                      <strong>{t("washDetail.name")}</strong> {wash.carWash.legalName}
                    </Typography>
                    <Typography>
                      <strong>{t("washDetail.location")}</strong>{" "}
                      {wash.carWash.location?.street ||
                        (wash.carWash.location as { streetName?: string } | undefined)
                          ?.streetName ||
                        "N/A"}
                    </Typography>
                    <Typography>
                      <strong>{t("washDetail.carWashId")}</strong> {wash.carWash._id}
                    </Typography>
                  </>
                ) : (
                  <Typography color="text.secondary">
                    {t("washDetail.noCarWash")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.serviceDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography>
                  <strong>{t("washDetail.serviceType")}</strong>{" "}
                  {wash.serviceType === "inner"
                    ? t("washDetail.serviceInterior")
                    : wash.serviceType === "outer"
                      ? t("washDetail.serviceExterior")
                      : t("washDetail.serviceFullService")}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.vehicleType")}</strong>{" "}
                  {wash.vehicleDetails.type || "N/A"}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.brand")}</strong> {wash.vehicleDetails.brand || "N/A"}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.model")}</strong> {wash.vehicleDetails.model || "N/A"}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.color")}</strong> {wash.vehicleDetails.color || "N/A"}
                </Typography>
                <Typography>
                  <strong>{t("washDetail.plateNumber")}</strong>{" "}
                  {wash.vehicleDetails.plateNumber || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.pricing")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h4" color="primary" gutterBottom>
                  {wash.price} ₼
                </Typography>
                <Typography>
                  <strong>{t("washDetail.adminCommission")}</strong> {wash.adminCommission} ₼
                </Typography>
                <Typography>
                  <strong>{t("washDetail.ownerReceives")}</strong>{" "}
                  {wash.price - wash.adminCommission} ₼
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.timeline")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography>
                  <strong>{t("washDetail.created")}</strong>{" "}
                  {new Date(wash.createdAt).toLocaleString()}
                </Typography>
                {wash.scheduledFor && (
                  <Typography>
                    <strong>{t("washDetail.scheduledFor")}</strong>{" "}
                    {new Date(wash.scheduledFor).toLocaleString()}
                  </Typography>
                )}
                {wash.startedAt && (
                  <Typography>
                    <strong>{t("washDetail.started")}</strong>{" "}
                    {new Date(wash.startedAt).toLocaleString()}
                  </Typography>
                )}
                {wash.completedAt && (
                  <Typography>
                    <strong>{t("washDetail.completed")}</strong>{" "}
                    {new Date(wash.completedAt).toLocaleString()}
                  </Typography>
                )}
                {wash.updatedAt && (
                  <Typography>
                    <strong>{t("washDetail.lastUpdated")}</strong>{" "}
                    {new Date(wash.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.customerReview")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {hasCustomerReview(wash) ? (
                  <>
                    <Typography>
                      <strong>{t("washDetail.rating")}</strong>{" "}
                      {isRatingSkipped(wash.washReview)
                        ? t("washingHistory.ratingSkipped")
                        : t("washingHistory.ratingOutOf", { rating: wash.washReview!.rating })}
                    </Typography>
                    {wash.washReview!.message && (
                      <Typography>
                        <strong>{t("washDetail.message")}</strong> {wash.washReview!.message}
                      </Typography>
                    )}
                    {!!wash.washReview!.tags?.length && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                        <strong>{t("washDetail.tags")}</strong>
                        {wash.washReview!.tags!.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">
                    {t("washDetail.noCustomerReview")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("washDetail.partnerReview")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {hasPartnerReview(wash) ? (
                  <>
                    {wash.partnerReview!.rating != null && (
                      <Typography>
                        <strong>{t("washDetail.rating")}</strong>{" "}
                        {t("washingHistory.ratingOutOf", { rating: wash.partnerReview!.rating })}
                      </Typography>
                    )}
                    {wash.partnerReview!.message && (
                      <Typography>
                        <strong>{t("washDetail.message")}</strong> {wash.partnerReview!.message}
                      </Typography>
                    )}
                    {typeof wash.partnerReview!.reviewedBy === "object" &&
                      wash.partnerReview!.reviewedBy && (
                        <Typography>
                          <strong>{t("washDetail.reviewedBy")}</strong>{" "}
                          {wash.partnerReview!.reviewedBy.firstName}{" "}
                          {wash.partnerReview!.reviewedBy.lastName}
                        </Typography>
                      )}
                    {wash.partnerReview!.reviewedAt && (
                      <Typography>
                        <strong>{t("washDetail.reviewedAt")}</strong>{" "}
                        {new Date(wash.partnerReview!.reviewedAt).toLocaleString()}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">
                    {t("washDetail.noPartnerReview")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {(wash.cancellationReason || wash.cancelledBy) && (
            <Grid size={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("washDetail.cancellation")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {wash.cancellationReason && (
                    <Typography>
                      <strong>{t("washDetail.cancellationReason")}</strong>{" "}
                      {wash.cancellationReason}
                    </Typography>
                  )}
                  {wash.cancellationComment && (
                    <Typography>
                      <strong>{t("washDetail.cancellationComment")}</strong>{" "}
                      {wash.cancellationComment}
                    </Typography>
                  )}
                  {wash.cancelledBy && (
                    <Typography>
                      <strong>{t("washDetail.cancelledBy")}</strong> {wash.cancelledBy}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Dialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("washDetail.updateStatusTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label={t("washDetail.newStatus")}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            margin="dense"
          >
            <MenuItem value="pending">{t("washDetail.statusPending")}</MenuItem>
            <MenuItem value="accepted">{t("washDetail.statusAccepted")}</MenuItem>
            <MenuItem value="in_progress">{t("washDetail.statusInProgress")}</MenuItem>
            <MenuItem value="completed">{t("washDetail.statusCompleted")}</MenuItem>
            <MenuItem value="cancelled">{t("washDetail.statusCancelled")}</MenuItem>
            <MenuItem value="not_arrive">{t("washDetail.statusNotArrived")}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updating || !newStatus}
          >
            {updating ? <CircularProgress size={24} /> : t("washDetail.update")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WashDetail;
